/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import type { FinVoiceVadSettings } from 'embercom/objects/ai-agent/fin-voice/fin-voice-server-events';
import type { AudioDeviceSettings, WebsocketState } from 'embercom/services/fin-voice-service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoicePipeline } from 'embercom/objects/ai-agent/fin-voice/fin-voice-pipeline-options';
import { FIN_VOICE_PIPELINE_OPTIONS } from 'embercom/objects/ai-agent/fin-voice/fin-voice-pipeline-options';
import type { FinVoiceRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type { FinVoiceVoiceModel } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-model-options';
import { FIN_VOICE_VOICE_MODEL_OPTIONS } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-model-options';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';

export interface SettingsArgs {
  connectionState: WebsocketState;
  selectedNumberConfigurationPhoneNumber: string | undefined;
  hasConfigChanges: boolean;
  vadSettings: FinVoiceVadSettings;
  selectedVoice: FinVoiceVoice;
  greeting: string;
  selectedPipeline: FinVoicePipeline;
  selectedRunbook: FinVoiceRunbook;
  selectedVoiceModel: FinVoiceVoiceModel;
  selectedLanguage: FinVoiceVoiceLanguage;
  audioDeviceSettings: AudioDeviceSettings;
  canUseFinVoicePlaygroundDev: boolean;
  updateVadSetting: (key: keyof FinVoiceVadSettings, value: number) => void;
  updateAudioDeviceSetting: (key: keyof AudioDeviceSettings, value: boolean) => void;
  updateVoice: (voice: FinVoiceVoice) => void;
  updateGreeting: (greeting: string) => void;
  updatePipeline: (pipeline: FinVoicePipeline) => void;
  updateRunbook: (runbook: FinVoiceRunbook) => void;
  updateVoiceModel: (voiceModel: FinVoiceVoiceModel) => void;
  updateLanguage: (language: FinVoiceVoiceLanguage) => void;
  useSemanticVad: boolean;
  updateUseSemanticVad: (value: boolean) => void;
  updateEagerness: (value: string) => void;
  semanticVadSettings?: {
    type: 'semantic_vad';
    eagerness: 'low' | 'medium' | 'high' | 'auto';
  };
  deployConfig: () => Promise<void>;
  resetConfig: () => void;
}

interface Signature {
  Args: SettingsArgs;
  Element: HTMLElement;
}

export default class Settings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked threshold = this.args.vadSettings.threshold;
  @tracked prefixPaddingMs = this.args.vadSettings.prefixPaddingMs;
  @tracked silenceDurationMs = this.args.vadSettings.silenceDurationMs;

  @tracked echoCancellation = this.args.audioDeviceSettings.echoCancellation;
  @tracked noiseSuppression = this.args.audioDeviceSettings.noiseSuppression;
  @tracked voiceIsolation = this.args.audioDeviceSettings.voiceIsolation;
  @tracked useSemanticVad = this.args.useSemanticVad ?? false;
  @tracked selectedEagernessOption = this.args.semanticVadSettings?.eagerness ?? 'medium';

  get selectedPipeline() {
    return this.args.selectedPipeline;
  }

  get selectedRunbook() {
    return this.args.selectedRunbook;
  }

  get selectedVoiceModel() {
    return this.args.selectedVoiceModel;
  }

  get selectedLanguage() {
    return this.args.selectedLanguage;
  }

  get isConfigSelected() {
    return !!this.args.selectedNumberConfigurationPhoneNumber;
  }

  get selectedVoice() {
    return this.args.selectedVoice;
  }

  get greeting() {
    return this.args.greeting;
  }

  set greeting(value: string) {
    this.args.updateGreeting(value);
  }

  get pipelineOptions() {
    return FIN_VOICE_PIPELINE_OPTIONS;
  }

  get voiceModelOptions() {
    return FIN_VOICE_VOICE_MODEL_OPTIONS;
  }

  get isSessionConnected() {
    return this.args.connectionState !== 'disconnected';
  }

  get eagernessOptions() {
    return [
      { value: 'low', text: this.intl.t('ai-agent.voice-playground.settings.low') },
      { value: 'medium', text: this.intl.t('ai-agent.voice-playground.settings.medium') },
      { value: 'high', text: this.intl.t('ai-agent.voice-playground.settings.high') },
      { value: 'auto', text: this.intl.t('ai-agent.voice-playground.settings.auto') },
    ];
  }

  @action onVoiceLanguageChange(voice: FinVoiceVoice, language: FinVoiceVoiceLanguage) {
    this.args.updateVoice(voice);
    this.args.updateLanguage(language);
  }

  @action onPipelineChange(event: FinVoicePipeline) {
    this.args.updatePipeline(event);
  }

  @action onRunbookChange(event: FinVoiceRunbook) {
    this.args.updateRunbook(event);
  }

  @action onVoiceModelChange(event: FinVoiceVoiceModel) {
    this.args.updateVoiceModel(event);
  }

  @action onThresholdChange(event: Event) {
    this.threshold = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateVadSetting('threshold', this.threshold);
  }

  @action onPrefixPaddingMsChange(event: Event) {
    this.prefixPaddingMs = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateVadSetting('prefixPaddingMs', this.prefixPaddingMs);
  }

  @action onSilenceDurationMsChange(event: Event) {
    this.silenceDurationMs = parseFloat((event.target as HTMLInputElement).value);
    this.args.updateVadSetting('silenceDurationMs', this.silenceDurationMs);
  }

  @action toggleEchoCancellation() {
    this.echoCancellation = !this.echoCancellation;
    this.args.updateAudioDeviceSetting('echoCancellation', this.echoCancellation);
  }

  @action toggleNoiseSuppression() {
    this.noiseSuppression = !this.noiseSuppression;
    this.args.updateAudioDeviceSetting('noiseSuppression', this.noiseSuppression);
  }

  @action toggleVoiceIsolation() {
    this.voiceIsolation = !this.voiceIsolation;
    this.args.updateAudioDeviceSetting('voiceIsolation', this.voiceIsolation);
  }

  @action toggleVadType() {
    this.useSemanticVad = !this.useSemanticVad;
    this.args.updateUseSemanticVad(this.useSemanticVad);
  }

  @action onEagernessChange(option: string) {
    this.selectedEagernessOption = option as 'low' | 'medium' | 'high' | 'auto';
    this.args.updateEagerness(option);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::Settings': typeof Settings;
    'ai-agent/voice-playground/settings': typeof Settings;
  }
}
